import type { Invoice, InvoiceAdvance, InvoiceCheckStatus } from '@/types/portal';

/** Demo-only conversion rates so mixed-currency totals can be compared in KES. */
const kesRates: Record<string, number> = {
  KES: 1,
  TZS: 0.052,
  UGX: 0.035,
  USD: 129,
};

export function toKesEquivalent(amount: number, currency: string): number {
  const rate = kesRates[currency] ?? 1;
  return Math.round(amount * rate);
}

export interface InvoiceCheckSummary {
  fails: number;
  warns: number;
  passes: number;
  label: string;
  tone: 'primary' | 'secondary' | 'accent';
}

export function summarizeChecks(invoice: Invoice): InvoiceCheckSummary {
  const count = (status: InvoiceCheckStatus) =>
    invoice.checks.filter((check) => check.status === status).length;
  const fails = count('fail');
  const warns = count('warn');
  const passes = count('pass');

  if (fails > 0) {
    return { fails, warns, passes, label: `${fails} blocking`, tone: 'primary' };
  }
  if (warns > 0) {
    return { fails, warns, passes, label: `${warns} to check`, tone: 'secondary' };
  }
  return { fails, warns, passes, label: 'All clear', tone: 'accent' };
}

/** A selected invoice is safe to approve when it carries no blocking checks. */
export function isApprovable(invoice: Invoice): boolean {
  return invoice.checks.every((check) => check.status !== 'fail');
}

/**
 * What is still owed to the creator once any early payout advance is netted off.
 * Falls back to the full total when no advance was drawn.
 */
export function balanceAfterAdvance(total: number, advance?: InvoiceAdvance): number {
  if (!advance) return total;
  return Math.max(0, total - advance.amount);
}